import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../notificationService';
import type { RetentionUnit } from '../../../types/retention';

export function useNotifications(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['notifications', page, limit, search],
    queryFn: () => notificationService.getNotifications(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useCreateNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationService.createNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationService.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/** Bulk-delete notifications. Renders its own toast, so the global one is hidden. */
export function useBulkDeleteNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => notificationService.bulkDeleteNotifications(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/** Preview how many notifications a retention purge would remove (drives the modal copy). */
export function useNotificationPurgePreview(
  value: number,
  unit: RetentionUnit,
  enabled = true,
) {
  return useQuery({
    queryKey: ['notifications', 'purgePreview', value, unit],
    queryFn: () => notificationService.previewPurgeNotifications(value, unit),
    enabled: enabled && value > 0,
    staleTime: 0,
    retry: false,
  });
}

/** Purge (permanently delete) notifications older than the retention window. */
export function usePurgeNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: ({ value, unit }: { value: number; unit: RetentionUnit }) =>
      notificationService.purgeNotifications(value, unit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
